import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, HStack, Text, Badge, Alert, AlertIcon, Spinner, Button, ButtonGroup, Checkbox,
  Table, Thead, Tbody, Tr, Th, Td, Code, Select,
} from "@chakra-ui/react";
import { twoWayBucketFileDiff, PhiClientError } from "../../utils/rdsPhi";
import DocumentMergeView from "./DocumentMergeView";
import { isNoteDocument } from "./docMerge";

/**
 * GitHub-style "what changes inside this file" for a .json item of the
 * two-way bucket sync (RDS file_diff.py). Shown under the item's details
 * before anything is merged:
 *
 *   Lines  -- unified diff of both copies (pretty-printed, keys sorted), old
 *             and new line numbers, red = removed, green = added;
 *   Fields -- every JSON path added / removed / changed, before -> after.
 *
 * "Before" is the copy the sync will overwrite and "after" the one written
 * over it, so the picture follows the item's current choice (Keep RDS /
 * Keep Supabase / as proposed). With no side chosen yet it shows RDS -> Supabase.
 */

export const isJsonItem = (item) => item?.kind === "object" && /\.json$/i.test(String(item.id || ""));

const NAME = { rds: "RDS", supabase: "Supabase" };

// target = columnTarget(item, "file", resolution) from the dialog.
export function diffOrientation(target) {
  if (target === "into_rds") return { fromSide: "rds", note: "RDS's copy is replaced with Supabase's" };
  if (target === "into_supabase") return { fromSide: "supabase", note: "Supabase's copy is replaced with RDS's" };
  if (target === "delete_rds") return { fromSide: "rds", note: "the file is deleted in RDS" };
  if (target === "delete_supabase") return { fromSide: "supabase", note: "the file is deleted in Supabase" };
  return { fromSide: "rds", note: "no side chosen yet -- comparing RDS with Supabase", neutral: true };
}

const WHOLE_FILE = 1000000;

function show(v) {
  if (v === undefined) return "";
  return typeof v === "string" ? JSON.stringify(v) : JSON.stringify(v, null, 2);
}

// Plain table cells with fixed styles: a whole note is thousands of lines,
// and four Chakra components per line (each building its CSS at runtime)
// is what made the browser stall. Draws LINE_PAGE lines, then "show more".
const LINE_PAGE = 1500;
const LINE_STYLE = {
  "+": { row: { background: "#f0fff4" }, num: { background: "#c6f6d5" }, sign: { color: "#276749" } },
  "-": { row: { background: "#fff5f5" }, num: { background: "#fed7d7" }, sign: { color: "#9b2c2c" } },
  " ": { row: {}, num: { background: "#f7fafc" }, sign: { color: "#a0aec0" } },
};
const NUM_CELL = { color: "#718096", textAlign: "right", padding: "0 4px", minWidth: 40, userSelect: "none" };
const TEXT_CELL = { padding: "0 4px", whiteSpace: "pre-wrap", wordBreak: "break-all" };
const HUNK_ROW = { background: "#ebf8ff", color: "#2b6cb0" };
const TESTID = { "+": "json-diff-line-add", "-": "json-diff-line-del", " ": "json-diff-line-ctx" };

function LineDiff({ hunks }) {
  const [limit, setLimit] = useState(LINE_PAGE);
  const rows = [];
  let total = 0;
  hunks.forEach((h, hi) => {
    total += h.lines.length;
    if (rows.length >= limit) return;
    rows.push(<tr key={`h${hi}`} style={HUNK_ROW}><td colSpan={4} style={{ padding: "2px 8px" }}>{h.header}</td></tr>);
    h.lines.forEach((l, li) => {
      if (rows.length >= limit) return;
      const st = LINE_STYLE[l.t] || LINE_STYLE[" "];
      rows.push(
        <tr key={`${hi}-${li}`} style={st.row} data-testid={TESTID[l.t] || TESTID[" "]}>
          <td style={{ ...NUM_CELL, ...st.num }}>{l.a ?? ""}</td>
          <td style={{ ...NUM_CELL, ...st.num }}>{l.b ?? ""}</td>
          <td style={{ padding: "0 4px", width: 14, userSelect: "none", ...st.sign }}>{l.t}</td>
          <td style={TEXT_CELL}>{l.text}</td>
        </tr>,
      );
    });
  });
  const shownLines = Math.min(limit, total + hunks.length);
  return (
    <Box maxH="460px" overflow="auto" border="1px solid" borderColor="gray.200" borderRadius="md" bg="white">
      <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "monospace", fontSize: 11 }}
             data-testid="json-diff-lines">
        <tbody>{rows}</tbody>
      </table>
      {total + hunks.length > shownLines && (
        <Button size="xs" variant="ghost" m={1} onClick={() => setLimit((n) => n + LINE_PAGE)}>
          Show {Math.min(LINE_PAGE, total + hunks.length - shownLines)} more lines
        </Button>
      )}
    </Box>
  );
}

const OP = { added: { scheme: "green", sign: "+" }, removed: { scheme: "red", sign: "−" }, changed: { scheme: "orange", sign: "~" } };

function FieldDiff({ changes }) {
  return (
    <Box maxH="460px" overflow="auto" border="1px solid" borderColor="gray.200" borderRadius="md" bg="white">
      <Table size="sm" data-testid="json-diff-fields">
        <Thead position="sticky" top={0} bg="gray.50">
          <Tr><Th>Field</Th><Th>Change</Th><Th>Before</Th><Th>After</Th></Tr>
        </Thead>
        <Tbody>
          {changes.map((c, i) => (
            <Tr key={i}>
              <Td><Code fontSize="xs" wordBreak="break-all">{c.path}</Code></Td>
              <Td><Badge colorScheme={OP[c.op]?.scheme} fontSize="9px">{c.op}</Badge></Td>
              <Td fontSize="xs" fontFamily="mono" whiteSpace="pre-wrap" wordBreak="break-word" maxW="320px"
                  bg={c.op !== "added" ? "red.50" : undefined}>{show(c.before)}</Td>
              <Td fontSize="xs" fontFamily="mono" whiteSpace="pre-wrap" wordBreak="break-word" maxW="320px"
                  bg={c.op !== "removed" ? "green.50" : undefined}>{show(c.after)}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}

// Which copy wins when a whole file is copied over the other.
const KEEP = { into_rds: "supabase", into_supabase: "rds", delete_rds: "supabase", delete_supabase: "rds" };

function errorText(err) {
  let msg = err?.message || String(err);
  if (err instanceof PhiClientError) {
    msg = err.statusCode === 404 ? "this RDS service has no file-diff route yet (restart/redeploy RDS)"
      : typeof err.detail === "string" && err.detail ? err.detail : err.message;
  }
  return msg;
}

/**
 * resolution "merge": the note is merged (DocumentMergeView) and onMerged
 * gets the merged .json; a file that is not a note calls onNotMergeable
 * once, so the dialog can fall back to copying it whole.
 *
 * Two requests, so a note costs only what is shown: the first brings both
 * documents and the field changes (no line diff); the line diff is asked
 * for only when Lines is opened (or the file is not a note, where Lines is
 * the default view). The documents are fetched once and kept, so switching
 * views or "Show whole file" never rebuilds the note page.
 */
function JsonFileDiff({ item, target, resolution, onMerged, onNotMergeable, loadChoices, onChoices, onRetry }) {
  const merging = resolution === "merge";
  const orient = diffOrientation(target);
  const fromSide = merging ? "rds" : orient.fromSide;
  const note = merging ? "merging both copies, nothing is replaced" : orient.note;
  const neutral = merging || orient.neutral;
  const toSide = fromSide === "rds" ? "supabase" : "rds";
  const [view, setView] = useState(null);
  const [whole, setWhole] = useState(false);
  const [state, setState] = useState({ loading: true });
  const [lines, setLines] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true });
    Promise.resolve()
      .then(() => twoWayBucketFileDiff(item.table, item.id, { fromSide, includeDocs: true, lines: false }))
      .then((data) => { if (!cancelled) setState({ data }); })
      .catch((err) => { if (!cancelled) setState({ error: errorText(err) }); });
    return () => { cancelled = true; };
  }, [item.table, item.id, fromSide]);

  const d = state.data;
  const docs = d?.documents;
  const rdsDoc = docs ? (fromSide === "rds" ? docs.before : docs.after) : null;
  const sbDoc = docs ? (fromSide === "rds" ? docs.after : docs.before) : null;
  const isNote = !!d && (isNoteDocument(rdsDoc) || isNoteDocument(sbDoc));
  const bothNotes = isNoteDocument(rdsDoc) && isNoteDocument(sbDoc);
  const shown = view || (isNote ? "document" : "lines");
  const wantLines = !!d && shown === "lines";

  useEffect(() => {
    if (!wantLines) return undefined;
    let cancelled = false;
    setLines({ loading: true });
    Promise.resolve()
      .then(() => twoWayBucketFileDiff(item.table, item.id, { fromSide, context: whole ? WHOLE_FILE : 3 }))
      .then((data) => { if (!cancelled) setLines({ data }); })
      .catch((err) => { if (!cancelled) setLines({ error: errorText(err) }); });
    return () => { cancelled = true; };
  }, [wantLines, item.table, item.id, fromSide, whole]);

  useEffect(() => {
    if (merging && d && !bothNotes && onNotMergeable) onNotMergeable();
  }, [merging, d, bothNotes, onNotMergeable]);

  const ld = lines?.data;
  return (
    <Box mt={3} data-testid="json-file-diff">
      <HStack spacing={2} mb={2} flexWrap="wrap">
        <Text fontSize="xs" fontWeight="bold">
          {merging ? "Merged note" : `Changes inside the file: ${neutral ? `${NAME.rds} ↔ ${NAME.supabase}` : `${NAME[fromSide]} before → after`}`}
        </Text>
        <Text fontSize="xs" color="gray.500">({note})</Text>
        {d && !d.identical && shown !== "document" && (
          <>
            <Badge colorScheme="green" fontSize="9px">+{d.counts.added} added</Badge>
            <Badge colorScheme="orange" fontSize="9px">~{d.counts.changed} changed</Badge>
            <Badge colorScheme="red" fontSize="9px">−{d.counts.removed} removed</Badge>
            {ld && (
              <Text fontSize="xs" color="gray.500">
                <Text as="span" color="green.600">+{ld.line_counts.lines_added}</Text>{" "}
                <Text as="span" color="red.600">−{ld.line_counts.lines_removed}</Text> lines
              </Text>
            )}
          </>
        )}
      </HStack>
      <HStack spacing={3} mb={2}>
        <ButtonGroup size="xs" isAttached variant="outline">
          {isNote && <Button isActive={shown === "document"} onClick={() => setView("document")}>Document</Button>}
          <Button isActive={shown === "fields"} onClick={() => setView("fields")}>Fields</Button>
          <Button isActive={shown === "lines"} onClick={() => setView("lines")}>Lines</Button>
        </ButtonGroup>
        {shown === "lines" && (
          <Checkbox size="sm" isChecked={whole} onChange={(e) => setWhole(e.target.checked)}>
            <Text fontSize="xs">Show whole file</Text>
          </Checkbox>
        )}
        {shown === "lines" && (
          <Text fontSize="10px" color="gray.500">
            Left numbers: {NAME[fromSide]}'s copy · right numbers: {NAME[toSide]}'s copy
          </Text>
        )}
      </HStack>
      {state.loading && <HStack><Spinner size="sm" /><Text fontSize="xs">Downloading both copies…</Text></HStack>}
      {state.error && (
        <Alert status="warning" fontSize="xs" data-testid="json-diff-error">
          <AlertIcon />
          <Text flex="1">Could not load this file: {state.error}</Text>
          {onRetry && <Button size="xs" ml={2} onClick={onRetry}>Retry</Button>}
        </Alert>
      )}
      {d && merging && !bothNotes && (
        <Alert status="info" fontSize="xs" mb={2}><AlertIcon />
          This file is not a clinic note on both sides, so it cannot be merged; it will be copied whole as proposed.
        </Alert>
      )}
      {d && (
        <>
          {(d.parse_error?.before || d.parse_error?.after) && (
            <Alert status="info" fontSize="xs" mb={2}>
              <AlertIcon />
              {d.parse_error.before ? `${NAME[fromSide]}'s copy is ${d.parse_error.before}. ` : ""}
              {d.parse_error.after ? `${NAME[toSide]}'s copy is ${d.parse_error.after}. ` : ""}
              Showing a plain text diff.
            </Alert>
          )}
          {/* While merging, the document view stays mounted (hidden) so the
              merged note keeps reaching the dialog when Lines is open. */}
          {isNote && (merging || shown === "document") && (
            <Box display={shown === "document" ? "block" : "none"}>
              <DocumentMergeView key={`${merging && bothNotes ? "merge" : "replace"}|${KEEP[target] || ""}`}
                                 rdsDoc={rdsDoc} supabaseDoc={sbDoc}
                                 mode={merging && bothNotes ? "merge" : "replace"}
                                 keep={KEEP[target]} onMerged={onMerged}
                                 loadChoices={loadChoices} onChoices={onChoices} />
            </Box>
          )}
          {shown === "fields" && (d.identical
            ? <Text fontSize="xs" color="gray.600">The JSON content is the same on both sides (only formatting or key order differs).</Text>
            : d.changes.length
              ? <FieldDiff changes={d.changes} />
              : <Text fontSize="xs" color="gray.600">No field-level view for this file — use Lines.</Text>)}
          {shown === "fields" && d.changes_truncated && (
            <Text fontSize="10px" color="orange.600" mt={1}>The diff is very large and was cut short.</Text>
          )}
          {shown === "lines" && lines?.loading && (
            <HStack><Spinner size="sm" /><Text fontSize="xs">Building the line diff…</Text></HStack>
          )}
          {shown === "lines" && lines?.error && (
            <Alert status="warning" fontSize="xs"><AlertIcon />Could not build the line diff: {lines.error}</Alert>
          )}
          {shown === "lines" && ld && (ld.identical
            ? <Text fontSize="xs" color="gray.600">The JSON content is the same on both sides (only formatting or key order differs).</Text>
            : <LineDiff hunks={ld.hunks} />)}
          {shown === "lines" && ld?.hunks_truncated && (
            <Text fontSize="10px" color="orange.600" mt={1}>The diff is very large and was cut short.</Text>
          )}
        </>
      )}
    </Box>
  );
}

export default React.memo(JsonFileDiff);

const TARGET_TEXT = {
  into_rds: { text: "Supabase → RDS", scheme: "purple" },
  into_supabase: { text: "RDS → Supabase", scheme: "blue" },
  delete_rds: { text: "delete in RDS", scheme: "red" },
  delete_supabase: { text: "delete in Supabase", scheme: "red" },
};

// A note both sides hold can be merged instead of copied over.
export const canMerge = (it) => isJsonItem(it) && it.fingerprint?.rds != null && it.fingerprint?.supabase != null;

/**
 * The review step before a Files apply: every selected .json file with its
 * diff, so the operator reads exactly what will be written. A note both
 * sides hold is MERGED by default (both copies' text kept, the operator
 * leaves out what should not survive); "Keep RDS" / "Keep Supabase" copy one
 * whole note over the other instead. A merged note must have been opened
 * (its merge built) before Apply is allowed.
 *
 * ONE file is open at a time: each open note is a live editor plus two
 * downloaded copies, and several at once is what made the page lag. The
 * keep / leave-out picks are kept per note here, so closing and reopening
 * one (or "Next note") does not lose them.
 *
 * Props: resolutionOf(it), onResolve(it, resolution), merged (key -> doc),
 * keyOf(it), onMerged(it, doc), onNotMergeable(it).
 */
export function JsonReviewList({
  items, targetOf, otherCount = 0, resolutionOf = () => "auto", onResolve, merged = {}, keyOf = (it) => it.id,
  onMerged, onNotMergeable,
}) {
  const [openId, setOpenId] = useState(() => items[0]?.id ?? null);
  // Bumped to remount (and so re-download) the open file after an error.
  const [retry, setRetry] = useState(0);
  const bumpRetry = useCallback(() => setRetry((n) => n + 1), []);

  // One stable callback set per item: the merge view reports on every change
  // and a fresh function each render would make that report loop.
  const latest = useRef({});
  latest.current = { onMerged, onNotMergeable };
  const choices = useRef({});
  const cbs = useRef({});
  const callbacks = (it) => {
    const k = keyOf(it);
    if (!cbs.current[k]) {
      cbs.current[k] = {
        merged: (doc) => latest.current.onMerged?.(it, doc),
        notMergeable: () => latest.current.onNotMergeable?.(it),
        choices: (c) => { choices.current[k] = c; },
        loadChoices: () => choices.current[k],
      };
    }
    return cbs.current[k];
  };

  const unreviewed = items.filter((it) => resolutionOf(it) === "merge" && !merged[keyOf(it)]);
  const openNext = () => {
    const next = unreviewed.find((it) => it.id !== openId) || unreviewed[0];
    if (next) setOpenId(next.id);
  };
  return (
    <Box data-testid="json-review">
      <HStack justify="space-between" mb={2} flexWrap="wrap">
        <Text fontSize="sm" fontWeight="bold">
          Review file changes: {items.length} JSON file{items.length === 1 ? "" : "s"} will change
          {otherCount > 0 ? ` (plus ${otherCount} other file${otherCount === 1 ? "" : "s"} copied whole)` : ""}
        </Text>
      </HStack>
      {unreviewed.length > 0 && (
        <Alert status="info" fontSize="xs" mb={2} data-testid="json-review-pending">
          <AlertIcon />
          <Text flex="1">
            Apply unlocks once every note set to Merge has been opened ({unreviewed.length} not reviewed yet).
            A note that will not load can be set to Keep RDS's or Keep Supabase's copy instead.
          </Text>
          <Button size="xs" colorScheme="blue" ml={2} onClick={openNext} data-testid="json-review-next">
            Next unreviewed note
          </Button>
        </Alert>
      )}
      {items.map((it) => {
        const resolution = resolutionOf(it);
        const merging = resolution === "merge";
        const target = targetOf(it);
        const dir = merging ? { text: "Merge both ways", scheme: "teal" }
          : TARGET_TEXT[target] || { text: "choose a side", scheme: "orange" };
        const isOpen = openId === it.id;
        const cb = callbacks(it);
        return (
          <Box key={`${it.table}|${it.id}`} borderWidth="1px" borderRadius="md" mb={2} data-testid="json-review-item">
            <HStack px={2} py={1} bg="gray.50" spacing={2}>
              <HStack spacing={2} flex="1" cursor="pointer" minW={0}
                      onClick={() => setOpenId((o) => (o === it.id ? null : it.id))}>
                <Text fontSize="xs" w="12px">{isOpen ? "▾" : "▸"}</Text>
                <Badge colorScheme={dir.scheme} fontSize="9px">{dir.text}</Badge>
                <Code fontSize="xs" wordBreak="break-all" bg="transparent">{it.table}/{String(it.id)}</Code>
                {merging && (merged[keyOf(it)]
                  ? <Badge colorScheme="green" fontSize="9px">reviewed</Badge>
                  : <Badge colorScheme="orange" fontSize="9px">not reviewed</Badge>)}
              </HStack>
              {canMerge(it) && onResolve && (
                <Select size="xs" w="150px" value={merging ? "merge" : resolution}
                        onChange={(e) => onResolve(it, e.target.value)} data-testid="json-review-resolution">
                  <option value="merge">Merge both copies</option>
                  <option value="rds">Keep RDS's copy</option>
                  <option value="supabase">Keep Supabase's copy</option>
                  {it.action !== "conflict" && <option value="auto">As proposed</option>}
                </Select>
              )}
            </HStack>
            {isOpen && (
              <Box px={2} pb={2}>
                <JsonFileDiff key={retry} item={it} target={target} resolution={resolution}
                              onMerged={cb.merged} onNotMergeable={cb.notMergeable}
                              loadChoices={cb.loadChoices} onChoices={cb.choices}
                              onRetry={bumpRetry} />
                {unreviewed.some((u) => u.id !== it.id) && (
                  <Button size="xs" mt={2} variant="outline" onClick={openNext}>Next unreviewed note ▸</Button>
                )}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
